<template>
  <v-modal
    version="new"
    :title="_getTitle()"
    :actions="getActions"
    classType="primary"
  >
    <WithLoader
      :isLoading="isLoading"
      classType="primary"
      extra-class="create-new-contact-modal"
    >
      <div>
        <v-form
          :name="name"
          :fields="fields"
          @submit="addContact"
          hideSubmitBtn
        ></v-form>
      </div>
    </WithLoader>
  </v-modal>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import { mapActions, mapGetters } from "vuex";
import cloneDeep from "lodash/cloneDeep";

import VModal from "@/components/VModal";
import VForm from "@/components/VForm";
import WithLoader from "@/components/WithLoader";
import withForm from "@/mixins/withForm";
import withToasted from "@/mixins/withToasted";
import withTags from "@/mixins/withTags";
import { requiredTags } from "@/utils/validations";
import { convertTagsToSelectListFormat, convertDataToApi } from "@/utils/util";
import {
  addressFormFields,
  generalInfoFormFields,
  apiToFormFieldNamesMap
} from "@/constants/forms";
import {
  convertDataFromApi,
  capitalizeFirstLetter,
  moveArrayItem
} from "@/utils/util";

export default Vue.component("ContactsCreateModal", {
  props: {
    isOpened: VueTypes.bool,
    contactId: VueTypes.string,
    contact: VueTypes.object
  },

  data() {
    return {
      name: "newContact",
      contactToEdit: null,
      initialContactValues: null
    };
  },

  mixins: [withToasted, withTags, withForm],

  components: {
    VModal,
    VForm,
    WithLoader
  },

  methods: {
    _performSendRequest() {
      this.validateForm();

      if (this.isValid) {
        return convertDataToApi("contact", this.formValues);
      }
    },

    _onCancel() {
      if (this.initialContactValues) {
        const initialCopy = cloneDeep(this.initialContactValues);
        this.updateValues({
          formName: this.name,
          fields: initialCopy
        });
      } else {
        this.clearForm();
      }

      this.$emit("close");
    },

    _handleSuccess(response, created = false) {
      this.toast(
        `Contact ${response.first_name} ${response.last_name} has been ${
          created ? "created" : "edited"
        }!`,
        "success",
        { icon: "address-book" }
      );

      const emitter = created ? "contactcreated" : "close";
      this.$emit(emitter);
      this.clearForm();
      this.contactToEdit = null;
    },

    _handleErrors(e) {
      if (typeof e === "string") {
        this.toast(e, "error");
      } else if (typeof e === "object") {
        if (e.description) {
          this.toast(capitalizeFirstLetter(e.description), "error");
        } else {
          const fields = Object.keys(e);
          const fieldsApiNameMap = apiToFormFieldNamesMap("contact");

          for (let field of fields) {
            this.syncErrors({
              formName: this.name,
              fieldName: fieldsApiNameMap[field],
              errors: e[field]
            });
            this.toast(capitalizeFirstLetter(e[field]), "error");
          }
        }
      }
    },

    _editContact() {
      const values = this._performSendRequest();

      if (values) {
        this.editContact({
          id: this.contactId,
          data: {
            ...values,
            id: this.contactId
          },
          onSuccess: this._handleSuccess,
          onError: this._handleErrors
        });
      }
    },

    _getTitle() {
      return this.contactId ? "Edit contact info" : "Add contact";
    },

    addContact() {
      const values = this._performSendRequest();

      if (values) {
        this.createContact({
          data: values,
          onSuccess: resp => this._handleSuccess(resp, true),
          onError: e => {
            this.syncErrorsFromServer(e);
            this.showServerErrors(e);
            this._handleErrors(e);
          }
        });
      }
    },

    ...mapActions("contacts", ["createContact", "fetchContact", "editContact"]),

    ...mapActions("tags", ["fetchFormTagsList", "deleteTag", "createTag"])
  },

  computed: {
    getActions() {
      return [
        {
          label: "Cancel",
          value: "cancel",
          noBorder: true,
          size: "sm",
          inverted: true,
          click: () => this._onCancel()
        },
        {
          label: this.contactId ? "Save" : "Add contact",
          value: "add",
          type: "primary",
          size: "wide",
          click: this.contactId ? this._editContact : this.addContact,
          disabled: !this.isValid || this.isLoading
        }
      ];
    },

    fields() {
      return [
        ...generalInfoFormFields,
        ...addressFormFields
          //remove doubled company field
          .filter(item => item.name !== "company")

          .map(item => {
            if (item.name === "country") {
              item.layout = "full";
              item.extraClassRoot = "contacts-create";
            }
            item.layout = "full";
            item.classType = "primary";
            return item;
          }),
        {
          name: "tags",
          label: "Tags",
          classType: "primary",
          multiple: true,
          elementType: "tags",
          isRequired: true,
          validate: [requiredTags],
          onAddHandler: this.handleAddTag,
          withDelete: true,
          onRemoveHandler: tag => this.handleDeleteTag(tag),
          selectItems: this.tags,
          maxLength: 255,
          extraClass: "contacts-create-tags",
          onSearchAsyncHandler: input =>
            this.fetchFormTagsList({ search: input }),
          isLoading: this._isTagsLoading
        },
        {
          name: "customData",
          label: "Custom Data",
          classType: "primary",
          elementType: "customData",
          extraClass: "contacts__data-wrapper"
        }
      ];
    },

    tags() {
      return convertTagsToSelectListFormat(this.tagsFromStore("formTags"));
    },

    _isTagsLoading() {
      return this.isMainTagsLoading || this.isFormTagsLoading;
    },

    ...mapGetters("account", ["getAccount"]),
    ...mapGetters("contacts", ["isLoading"]),
    ...mapGetters("tags", {
      tagsFromStore: "tags",
      isMainTagsLoading: "isLoading",
      isFormTagsLoading: "isFormTagsLoading"
    })
  },

  created() {
    this.fetchFormTagsList();
    // Initialize form if it's not exists
    // to let auto-fill fields when clicked "Edit contact"
    if (!this.getForm()) {
      this.registerForm({ formName: this.name, fields: this.fields });
    }

    if (this.contactId) {
      const contact = convertDataFromApi("contact", this.contact);

      const { fromAddress, ...rest } = contact;
      const data = {
        ...rest,
        ...fromAddress,
        customData: rest.custom_data
      };

      this.initializeForm({
        formName: this.name,
        fields: data
      });

      this.initialContactValues = cloneDeep(data);
    }
  }
});
</script>

<style lang="scss" scoped>
.custom-tags__wrapper {
  margin-top: 20px;

  h3 {
    margin-bottom: 10px;
  }
}

.create-new-contact-modal {
  @include customScrollbar();
  padding-right: 15px;
  overflow-y: auto;
  height: auto;
}
</style>

<style lang="scss">
.contacts__data-wrapper {
  margin-bottom: 20px;
}

.contacts-create-tags {
  .input-container {
    margin-bottom: 15px;
  }
}
</style>
